// ============================================================
// WHAT COSTS COHESION (Squad Mode stage 6, behind the house rule
// `cohesionDamage`, on by default). A brother in Squad Mode costs the pool
// ONE (never more, however many rounds land) when a hit on him deals 10 or
// more damage before reduction from an Accurate ranged weapon, a Blast
// (the weapon's rule or a blast template) or a Devastating weapon. A Blast
// over three brothers is three. The (acting) leader on that stage resists
// each with a Challenging (+0) Command or Fellowship test, the better of
// the two (ruled 2026-10-23); with no leader answered there, the cost
// lands, with a line.
//
// Fear reaches the pool only through the (acting) leader: his failed dread
// test costs the creature's Fear Rating (D12).
//
// Pure. Zero React dependency.
// ============================================================
import type { Character } from '../character/types';
import { calcSkillTarget } from '../characteristicTests';
import { hasWS } from '../weaponSpecials';

/** What a hit's attack was, as far as Cohesion cares. */
export interface CohesionAttack {
  specialRules: readonly string[];
  isMelee: boolean;
  /** The template it rolled, if any (`areaRolled.shape`). */
  areaShape?: string | null;
}

/** The damage before reduction that a hit must reach. */
const COHESION_DAMAGE = 10;

/** Does the attack's kind cost Cohesion at all (before the damage is known)? */
export function costlyKind(a: CohesionAttack): boolean {
  if (a.areaShape === 'blast' || hasWS(a.specialRules as string[], 'Blast')) return true;
  if (hasWS(a.specialRules as string[], 'Devastating')) return true;
  return !a.isMelee && hasWS(a.specialRules as string[], 'Accurate');
}

/** One brother's share of a burst: 1 when any hit on him reached the mark, else 0. */
export function brotherCost(a: CohesionAttack, rawDamages: readonly number[]): number {
  return costlyKind(a) && rawDamages.some((d) => d >= COHESION_DAMAGE) ? 1 : 0;
}

/** The leader's resist: the better of his Command and his Fellowship, at Challenging (+0). */
export function resistTarget(leader: Character): { target: number; via: 'Command' | 'Fellowship' } {
  const command = calcSkillTarget(leader, 'command').base;
  const fel = leader.characteristics.fel.value;
  return command > fel ? { target: command, via: 'Command' } : { target: fel, via: 'Fellowship' };
}

/** The resist test's verdict on a d100, and the line it earns. */
export function resists(leader: Character, d100: number): { passed: boolean; line: string } {
  const { target, via } = resistTarget(leader);
  const passed = d100 <= target;
  const verdict = passed ? 'holds the squad together' : 'cannot hold them';
  return { passed, line: `${leader.name} tests ${via} (${d100} vs ${target}): ${verdict}.` };
}

/** An attack's hits on one brother, as the hit pipeline has them: the weapon's rules and each hit's raw damage. */
export interface HitsOnBrother {
  slot: { specialRules?: readonly string[]; weaponType?: string };
  hitsAfterDefence: readonly { rawDamage: number }[];
  areaRolled?: { shape?: string } | null;
}

/** What one attack costs the pool through this brother: 0 or 1. */
export function cohesionCostOf(h: HitsOnBrother): number {
  const attack = {
    specialRules: h.slot.specialRules ?? [],
    isMelee: h.slot.weaponType === 'melee',
    areaShape: h.areaRolled?.shape ?? null,
  };
  return brotherCost(
    attack,
    h.hitsAfterDefence.map((x) => x.rawDamage),
  );
}
